import { formatInTimeZone } from 'date-fns-tz';
import { AlertTriangle, MoreHorizontal } from 'lucide-react';
import { type ReactNode } from 'react';

import {
    getAvailabilityType,
    type SourceLabel,
    type TimeboxedAvailabilityEntry,
    type TimeboxedAvailabilityEntrySegment,
    type TimeboxedAvailabilitySegment,
    type TimelineItemType,
    type WeeklyAvailabilityEntry,
    type WeeklyAvailabilityEntrySegment,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { cn } from '@apps/web/lib/utils';

const SCHEDULE_DETAIL_CONTAINER_CLASS = 'bg-muted/20 py-1.5';

type BadgeVariant = 'success' | 'destructive' | 'secondary';

const TYPE_BADGE_CONFIG: Record<
    TimelineItemType,
    { label: string; variant: BadgeVariant }
> = {
    availability: {
        label: 'Available',
        variant: 'success',
    },
    unavailability: {
        label: 'Unavailable',
        variant: 'destructive',
    },
    shift: {
        label: 'Shift',
        variant: 'secondary',
    },
};

function getTypeBadgeConfig(type: TimelineItemType) {
    return TYPE_BADGE_CONFIG[type];
}

function AvailabilityActionMenu({
    onEdit,
    onDelete,
}: {
    readonly onEdit: () => void;
    readonly onDelete: () => void;
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 shrink-0"
                >
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>
                <DropdownMenuItem variant="destructive" onClick={onDelete}>
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function ScheduleActionCell({ children }: { readonly children?: ReactNode }) {
    return (
        <div className="flex h-5 items-center justify-end">
            {children ?? <span aria-hidden={true} className="h-5 w-5" />}
        </div>
    );
}

export function ConflictBadge() {
    return (
        <Badge
            variant="outline"
            className="border-amber-300 bg-amber-50 px-1 py-0 text-[10px] leading-4 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300"
            title="Sources disagree on this availability window."
        >
            <AlertTriangle className="mr-0.5 h-3 w-3" />
            Conflict
        </Badge>
    );
}

function SourceLabelText({
    sourceLabel,
}: {
    readonly sourceLabel: SourceLabel;
}) {
    return sourceLabel ? (
        <span
            className="text-muted-foreground block max-w-full min-w-0 text-right text-xs leading-4 break-normal whitespace-normal"
            title={sourceLabel}
        >
            {sourceLabel}
        </span>
    ) : (
        <span className="text-muted-foreground text-right text-xs leading-4">
            --
        </span>
    );
}

function ScheduleSourceCell({
    sourceLabel,
    hasConflict = false,
}: {
    readonly sourceLabel: SourceLabel;
    readonly hasConflict?: boolean;
}) {
    return (
        <div className="flex min-w-0 flex-col items-end justify-center gap-0.5 pl-3">
            {hasConflict ? <ConflictBadge /> : null}
            <SourceLabelText sourceLabel={sourceLabel} />
        </div>
    );
}

function StatusBadge({ type }: { readonly type: TimelineItemType }) {
    const badge = getTypeBadgeConfig(type);

    return (
        <Badge
            variant={badge.variant}
            className="px-1.5 py-0 text-[11px] leading-4"
        >
            {badge.label}
        </Badge>
    );
}

// Rows of the calendar chip-details dialog: per-entry provenance with
// edit/delete for manual one-time entries. Recurring (weekly-pattern)
// occurrences carry synthetic ids and are never editable here — their
// editor is the Weekly pattern view. Unlike the weekly list rows, these
// have no expander column, so content sits flush with the dialog text.
const DETAIL_ROW_GRID_CLASS =
    'grid grid-cols-[5rem_minmax(6rem,0.95fr)_minmax(0,1fr)_1.25rem] items-center gap-x-1.5 gap-y-1';

export function TimeboxedEntryDetails({
    entrySegments,
    onEditEntry,
    onDeleteEntry,
}: {
    readonly entrySegments: ReadonlyArray<TimeboxedAvailabilityEntrySegment>;
    // Omit both to render read-only (e.g. the Overview details dialog).
    readonly onEditEntry?: (entry: TimeboxedAvailabilityEntry) => void;
    readonly onDeleteEntry?: (entry: TimeboxedAvailabilityEntry) => void;
}) {
    return (
        <div className={SCHEDULE_DETAIL_CONTAINER_CLASS}>
            <div className="space-y-1">
                {entrySegments.map((segment) => {
                    const sourceLabel = segment.entry.recurring
                        ? segment.sourceLabel
                            ? `${segment.sourceLabel} · Weekly pattern`
                            : 'Weekly pattern'
                        : segment.sourceLabel;
                    return (
                        <div
                            key={segment.id}
                            className={cn(
                                DETAIL_ROW_GRID_CLASS,
                                'min-h-6 px-1.5 py-1.5 text-xs',
                            )}
                        >
                            <div className="flex justify-start">
                                <StatusBadge
                                    type={getAvailabilityType(segment.status)}
                                />
                            </div>
                            <div className="text-foreground whitespace-nowrap tabular-nums">
                                {segment.timeLabel}
                            </div>
                            <ScheduleSourceCell sourceLabel={sourceLabel} />
                            <ScheduleActionCell>
                                {segment.source === 'manual' &&
                                !segment.entry.recurring &&
                                onEditEntry &&
                                onDeleteEntry ? (
                                    <AvailabilityActionMenu
                                        onEdit={() =>
                                            onEditEntry(segment.entry)
                                        }
                                        onDelete={() =>
                                            onDeleteEntry(segment.entry)
                                        }
                                    />
                                ) : null}
                            </ScheduleActionCell>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Details dialog for a calendar availability chip, shared by the caregiver
 * Overview and Availability tabs. A recurring window is described as the
 * rule ("Saturdays") rather than one dated instance. Omit the edit/delete
 * handlers to render read-only.
 */
export function AvailabilityDetailsDialog({
    segment,
    timezone,
    onClose,
    onEditEntry,
    onDeleteEntry,
}: {
    readonly segment: TimeboxedAvailabilitySegment | null;
    readonly timezone: string;
    readonly onClose: () => void;
    readonly onEditEntry?: (entry: TimeboxedAvailabilityEntry) => void;
    readonly onDeleteEntry?: (entry: TimeboxedAvailabilityEntry) => void;
}) {
    return (
        <Dialog
            open={segment !== null}
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        Availability details
                        {segment?.hasConflict ? <ConflictBadge /> : null}
                    </DialogTitle>
                    <DialogDescription>
                        {segment
                            ? segment.recurring
                                ? `${formatInTimeZone(segment.startTime, timezone, 'EEEE')}s`
                                : segment.dayLabel
                            : null}
                    </DialogDescription>
                </DialogHeader>
                {segment ? (
                    <TimeboxedEntryDetails
                        entrySegments={segment.entrySegments}
                        onEditEntry={onEditEntry}
                        onDeleteEntry={onDeleteEntry}
                    />
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

// Rows of the weekly-pattern block dialog: one row per underlying rule,
// with edit/delete for manual rules; EHR-synced rules stay read-only.
export function WeeklyEntryDetails({
    entrySegments,
    onEditEntry,
    onDeleteEntry,
}: {
    readonly entrySegments: ReadonlyArray<WeeklyAvailabilityEntrySegment>;
    readonly onEditEntry: (entry: WeeklyAvailabilityEntry) => void;
    readonly onDeleteEntry: (entry: WeeklyAvailabilityEntry) => void;
}) {
    return (
        <div className={SCHEDULE_DETAIL_CONTAINER_CLASS}>
            <div className="space-y-1">
                {entrySegments.map((segment) => {
                    return (
                        <div
                            key={segment.id}
                            className={cn(
                                DETAIL_ROW_GRID_CLASS,
                                'min-h-6 px-1.5 py-1.5 text-xs',
                            )}
                        >
                            <div className="flex justify-start">
                                <StatusBadge
                                    type={getAvailabilityType(segment.status)}
                                />
                            </div>
                            <div className="text-foreground whitespace-nowrap tabular-nums">
                                {segment.timeLabel}
                            </div>
                            <ScheduleSourceCell
                                sourceLabel={segment.sourceLabel}
                            />
                            <ScheduleActionCell>
                                {segment.source === 'manual' ? (
                                    <AvailabilityActionMenu
                                        onEdit={() =>
                                            onEditEntry(segment.entry)
                                        }
                                        onDelete={() =>
                                            onDeleteEntry(segment.entry)
                                        }
                                    />
                                ) : null}
                            </ScheduleActionCell>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
